import { useEffect, useMemo, useState } from "react";

/**
 * Route-entry budget for Position Tracker's production-sized lists. The first
 * committed frame gets enough rows to fill the normal desktop viewport, then
 * each animation frame admits one bounded batch. Data derivation still runs
 * against the complete live set; this limits React/DOM mount work only.
 */
export const INITIAL_PROGRESSIVE_ROWS = 48;
export const PROGRESSIVE_ROWS_PER_FRAME = 64;

interface ProgressiveState {
  key: string;
  limit: number;
}

export function nextProgressiveRowLimit(
  current: number,
  total: number,
  batchSize = PROGRESSIVE_ROWS_PER_FRAME,
): number {
  return Math.min(total, Math.max(0, current) + Math.max(1, batchSize));
}

export function useProgressiveRowLimit(
  total: number,
  resetKey: string,
): number {
  const normalizedTotal = Math.max(0, total);
  const initialLimit = Math.min(normalizedTotal, INITIAL_PROGRESSIVE_ROWS);
  const [state, setState] = useState<ProgressiveState>(() => ({
    key: resetKey,
    limit: initialLimit,
  }));

  // A changed filter/view/sort key must be bounded on that same render. Waiting
  // for the effect would briefly mount the prior list's full row count.
  const visibleLimit =
    state.key === resetKey
      ? Math.min(state.limit, normalizedTotal)
      : initialLimit;

  useEffect(() => {
    if (state.key !== resetKey) {
      const frame = window.requestAnimationFrame(() => {
        setState({ key: resetKey, limit: initialLimit });
      });
      return () => window.cancelAnimationFrame(frame);
    }
    if (visibleLimit >= normalizedTotal) return;

    const frame = window.requestAnimationFrame(() => {
      setState((current) => {
        if (current.key !== resetKey) return current;
        return {
          key: current.key,
          limit: nextProgressiveRowLimit(current.limit, normalizedTotal),
        };
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [initialLimit, normalizedTotal, resetKey, state.key, visibleLimit]);

  return visibleLimit;
}

export function useProgressiveRows<T>(
  rows: readonly T[],
  resetKey: string,
): readonly T[] {
  const limit = useProgressiveRowLimit(rows.length, resetKey);
  return useMemo(
    () => (limit >= rows.length ? rows : rows.slice(0, limit)),
    [limit, rows],
  );
}
